/**
 * API interceptors for automatic JSON Schema validation
 * Validates responses from API before processing
 */

import { AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import {
  validateBudgetPositions,
  validateExternalPositions,
  validateCalculationFigures,
  validateUnitHoursData,
  validateBillingRatesData,
  validatePhases,
  validateHourData,
  validateAllocationOfPatients,
  ValidationResult
} from '../../utils/jsonSchemaValidation';

// Check if frontend validation is enabled
const isFrontendValidationEnabled = (): boolean => {
  // In test environment, use process.env
  if (typeof process !== 'undefined' && process.env.NODE_ENV === 'test') {
    return process.env.VITE_ENABLE_FRONTEND_VALIDATION === 'true';
  }
  
  // In browser environment, check for Vite's env variable
  // Vite replaces import.meta.env at build time, but we need a runtime check
  // that won't break Jest's parser
  try {
    // Check if we're in a Vite environment by looking for injected env vars
    const globalAny = globalThis as any;
    
    // Try to get the value from a custom global that we could set
    if (globalAny.VITE_ENV && globalAny.VITE_ENV.VITE_ENABLE_FRONTEND_VALIDATION) {
      return globalAny.VITE_ENV.VITE_ENABLE_FRONTEND_VALIDATION === 'true';
    }
    
    // For Vite dev server, check localStorage as a fallback
    if (typeof window !== 'undefined' && window.localStorage) {
      const stored = window.localStorage.getItem('VITE_ENABLE_FRONTEND_VALIDATION');
      if (stored !== null) {
        return stored === 'true';
      }
    }
  } catch {
    // Silent fail - validation disabled
  }
  
  // Default to true in development, false in production
  return typeof process !== 'undefined' && process.env.NODE_ENV === 'development';
};

// Define which endpoints need validation and their validators
const ENDPOINT_VALIDATORS: Record<string, Record<string, (data: any) => ValidationResult>> = {
  '/api/budget/': {
    budget_positions: validateBudgetPositions,
    external_positions: validateExternalPositions,
    calculation_figures: validateCalculationFigures,
    unit_hours_data: validateUnitHoursData,
    billing_rates_data: validateBillingRatesData,
    phases: validatePhases,
    allocation_of_patients: validateAllocationOfPatients,
  },
  '/api/budget-module/projects/': {
    budget_positions: validateBudgetPositions,
    external_positions: validateExternalPositions,
    calculation_figures: validateCalculationFigures,
    unit_hours_data: validateUnitHoursData,
    billing_rates_data: validateBillingRatesData,
    phases: validatePhases,
    allocation_of_patients: validateAllocationOfPatients,
  },
  // Budget templates (issue #98) carry the same three JSON shapes as a budget.
  // Note billing_rates_data entries additionally carry an `is_override` flag;
  // the billingRate definition allows extra properties, so the same validator applies.
  '/api/budget-module/templates/': {
    budget_positions: validateBudgetPositions,
    external_positions: validateExternalPositions,
    billing_rates_data: validateBillingRatesData,
  },
  '/api/budget/positions/': {
    hour_data: validateHourData,
  },
  '/api/budget/category-department-roles/': {
    hour_data: validateHourData,
  },
  '/api/budget-module/settings/category-department-roles/': {
    hour_data: validateHourData,
  },
  '/api/settings/departments/': {
    hour_data: validateHourData,
  },
  '/api/budget-module/projects/.*\/positions/': {
    enabled: (data: any) => ({ valid: typeof data === 'boolean', errors: data !== undefined && typeof data !== 'boolean' ? [{ field: 'enabled', message: 'Must be a boolean value' }] : [] }),
  }
};

/**
 * Find validator configuration for a given URL
 */
function getValidatorsForUrl(url: string): Record<string, (data: any) => ValidationResult> | null {
  // Sort endpoints by length (longest first) to match most specific first
  const sortedEndpoints = Object.entries(ENDPOINT_VALIDATORS)
    .sort(([a], [b]) => b.length - a.length);
  
  for (const [endpoint, validators] of sortedEndpoints) {
    if (url.includes(endpoint.replace(/\/$/, ''))) {
      return validators;
    }
  }
  return null;
}

/**
 * Validate response data against schemas
 */
export function validateResponseData(response: AxiosResponse): void {
  if (!isFrontendValidationEnabled()) {
    return;
  }

  const validators = getValidatorsForUrl(response.config.url || '');
  
  if (!validators || !response.data) {
    return;
  }

  const validationErrors: Record<string, string[]> = {};
  
  // Handle both single object and array responses
  const dataToValidate = Array.isArray(response.data.results) 
    ? response.data.results 
    : Array.isArray(response.data) 
    ? response.data 
    : [response.data];

  for (const item of dataToValidate) {
    for (const [field, validator] of Object.entries(validators)) {
      if (field in item) {
        const result = validator(item[field]);
        if (!result.valid && result.errors) {
          if (!validationErrors[field]) {
            validationErrors[field] = [];
          }
          validationErrors[field].push(...result.errors.map((e: any) => `${e.field}: ${e.message}`));
        }
      }
    }
  }

  // Log validation errors in development
  if (Object.keys(validationErrors).length > 0) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('⚠️ API Response Validation Errors:', {
        url: response.config.url,
        errors: validationErrors
      });
    }
    
    // Add validation warnings to response (non-blocking)
    if (response.data && typeof response.data === 'object') {
      response.data._validationWarnings = validationErrors;
    }
  }
}

/**
 * Validate request payload before sending
 */
export function validateRequestPayload(config: InternalAxiosRequestConfig): void {
  if (!isFrontendValidationEnabled()) {
    return;
  }

  // Only validate JSON requests
  if (config.headers['Content-Type'] !== 'application/json' || !config.data) {
    return;
  }

  // Only validate POST, PUT, PATCH
  if (!['post', 'put', 'patch'].includes(config.method?.toLowerCase() || '')) {
    return;
  }

  const validators = getValidatorsForUrl(config.url || '');
  if (!validators) {
    return;
  }

  const validationErrors: Record<string, string[]> = {};

  for (const [field, validator] of Object.entries(validators)) {
    if (field in config.data) {
      const result = validator(config.data[field]);
      if (!result.valid && result.errors) {
        validationErrors[field] = result.errors.map((e: any) => {
          // Include the actual data in the error message
          const dataPreview = e.value ? `\nData: ${typeof e.value === 'string' ? e.value : JSON.stringify(e.value, null, 2)}` : '';
          return `${e.field}: ${e.message}${dataPreview}`;
        });
      }
    }
  }

  // Throw error if validation fails (blocking)
  if (Object.keys(validationErrors).length > 0) {
    console.error('🔴 Frontend Request Validation Failed:', {
      url: config.url,
      method: config.method?.toUpperCase(),
      errors: validationErrors,
      payload: config.data
    });
    
    const error = new Error('Frontend validation failed - data does not match required schema');
    (error as any).validationErrors = validationErrors;
    (error as any).config = config;
    throw error;
  }
}

/**
 * Response interceptor for validation
 */
export const responseValidationInterceptor = (response: AxiosResponse): AxiosResponse => {
  try {
    validateResponseData(response);
  } catch (error) {
    console.error('Error in response validation:', error);
  }
  return response;
};

/**
 * Request interceptor for validation
 */
export const requestValidationInterceptor = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  try {
    validateRequestPayload(config);
  } catch (error: any) {
    if (error.validationErrors) {
      // Frontend validation caught the error before sending to backend
      console.error('🛑 FRONTEND VALIDATION BLOCKED REQUEST:', {
        url: config.url,
        method: config.method?.toUpperCase(),
        message: 'Request was blocked by frontend validation before reaching the backend',
        validationErrors: error.validationErrors,
        payload: config.data
      });
      
      // Transform to axios error format that matches backend response structure
      const axiosError: AxiosError = {
        name: 'ValidationError',
        message: 'Frontend validation failed - request blocked',
        config: config as any,
        isAxiosError: true,
        toJSON: () => ({}),
        response: {
          data: {
            // Match backend error format for consistency
            budget_positions: error.validationErrors.budget_positions || [],
            external_positions: error.validationErrors.external_positions || [],
            calculation_figures: error.validationErrors.calculation_figures || [],
            unit_hours_data: error.validationErrors.unit_hours_data || [],
            billing_rates_data: error.validationErrors.billing_rates_data || [],
            phases: error.validationErrors.phases || [],
            hour_data: error.validationErrors.hour_data || [],
            _frontendValidation: true // Flag to identify frontend validation errors
          },
          status: 400,
          statusText: 'Frontend Validation Failed',
          headers: {},
          config: config as any
        }
      };
      
      throw axiosError;
    }
    throw error;
  }
  return config;
};

/**
 * Error interceptor to handle validation errors
 */
export const errorValidationInterceptor = (error: AxiosError): Promise<never> => {
  // Add validation context to errors if available
  if (error.response?.data && typeof error.response.data === 'object') {
    const data = error.response.data as any;
    
    // Check if this is a frontend validation error
    if (data._frontendValidation) {
      console.error('🛑 FRONTEND VALIDATION ERROR:', {
        url: error.config?.url,
        message: 'Request was blocked by frontend validation',
        validationErrors: {
          budget_positions: data.budget_positions,
          external_positions: data.external_positions,
          calculation_figures: data.calculation_figures,
          unit_hours_data: data.unit_hours_data,
          billing_rates_data: data.billing_rates_data,
          phases: data.phases,
          hour_data: data.hour_data
        }
      });
    } else if (data.validation_errors || data.budget_positions || data.external_positions) {
      // Backend validation errors
      console.error('🔴 BACKEND VALIDATION ERRORS:', {
        url: error.config?.url,
        message: 'Backend rejected the request after validation',
        backendErrors: {
          validation_errors: data.validation_errors,
          budget_positions: data.budget_positions,
          external_positions: data.external_positions,
          calculation_figures: data.calculation_figures,
          unit_hours_data: data.unit_hours_data,
          billing_rates_data: data.billing_rates_data,
          phases: data.phases,
          hour_data: data.hour_data
        }
      });
    }
  }
  return Promise.reject(error);
};